import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { IconButton, Typography } from '@mui/material';

import { DETAIL_LABEL_CLASS } from '@/components/layout/DetailField';
import type { Signature } from '@/features/annual-review';
import { cn } from '@/shared/lib/cn';

interface SignatureTileProps {
  signature: Signature;
  /** 1-based, and used in every accessible name on the tile. */
  position: number;
  onView: () => void;
  onRemove: () => void;
  /** True while a delete is in flight anywhere in the card. */
  isDeleting: boolean;
  /** The last signature cannot be removed — see `SignaturesCard`. */
  isOnlySignature: boolean;
}

/**
 * One saved signature, presented as the sheet of paper it stands in for.
 *
 * The preview sits on `surface` — the paper token, white — rather than on
 * `surface-subtle`: signatures we export are dark strokes on transparency (see
 * `signatureImage.ts`), so a tinted plate would be the app tinting the
 * director's ink, not the image carrying its own background. It is also what
 * the signed document shows, which makes the tile a preview rather than an
 * approximation.
 */
export const SignatureTile = ({
  signature,
  position,
  onView,
  onRemove,
  isDeleting,
  isOnlySignature,
}: SignatureTileProps) => (
  <li className="flex flex-col overflow-hidden rounded-xl border border-divider bg-surface transition-shadow focus-within:shadow-card hover:shadow-card">
    <button
      type="button"
      aria-label={`View signature ${String(position)} larger`}
      onClick={onView}
      className="group relative flex h-28 items-center justify-center bg-surface p-4"
    >
      {/* A ruled baseline, as on the paper this replaces — it gives the
          strokes something to sit on so a light signature does not float in
          an empty white box. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-4 bottom-4 border-b border-dashed border-divider"
      />

      <img
        src={signature.base64Image}
        alt={`Saved signature ${String(position)}`}
        className="relative max-h-full max-w-full object-contain"
      />

      {/* Hover dims the plate on a mouse; on touch there is no hover, so the
          badge alone has to carry the affordance. */}
      <span className="absolute inset-0 bg-content/0 transition-colors group-hover:bg-content/10 group-focus-visible:bg-content/10" />
      <span className="absolute right-2 bottom-2 flex size-7 items-center justify-center rounded-full bg-content/55 text-surface backdrop-blur-sm [&>svg]:text-[15px]">
        <ZoomOutMapIcon />
      </span>
    </button>

    {/* The row is sized by the icon button, which the theme holds at
        MIN_TOUCH_TARGET — padding it any tighter would make the button
        overflow its own footer. */}
    <div className="flex min-h-11 items-center justify-between gap-2 border-t border-divider bg-surface-subtle pr-1 pl-3">
      <Typography
        variant="caption"
        color="text.secondary"
        className={cn('truncate', DETAIL_LABEL_CLASS)}
      >
        Signature {position}
      </Typography>
      <IconButton
        aria-label={
          isOnlySignature
            ? 'Remove signature (unavailable — this is your only signature)'
            : `Remove signature ${String(position)}`
        }
        onClick={onRemove}
        disabled={isDeleting || isOnlySignature}
        size="small"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </div>
  </li>
);
